import React, { useState, useMemo } from 'react';
import { 
  ShieldIcon, 
  UserIcon, 
  CheckCircleIcon, 
  XCircleIcon, 
  AlertCircleIcon, 
  RefreshCwIcon, 
  Loader2, 
  TrashIcon,
  EyeIcon,
  ServerIcon,
  UserPlusIcon
} from 'lucide-react';
import { 
  usePermissionCheck, 
  useChannelPermissionCheck, 
  useUserLevel, 
  useGuildPermissions, 
  getPermissionLevelInfo, 
  usePermissionOperations,
  useDiscordGuilds,
  useDiscordGuildMembers,
  useDiscordGuildChannels
} from '../hooks/usePermissions.js';
import { useAuth } from '../contexts/AuthContext.js';
import type { SupportedBotName } from '@discord-bot-platform/service-client';
import { Button } from '@randompedestrian07/ui';
import { Card } from '@randompedestrian07/ui';
import { Alert } from '@randompedestrian07/ui';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from '@randompedestrian07/ui';
import { Input } from '@randompedestrian07/ui';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@randompedestrian07/ui';
import { Badge } from '@randompedestrian07/ui';

export const PermissionsPage = () => {
  // Auth context
  const { user } = useAuth();
  
  // Main states
  const [selectedGuildId, setSelectedGuildId] = useState<string>('');
  const [selectedBot, setSelectedBot] = useState<SupportedBotName>('hotel-bull');
  const [memberSearch, setMemberSearch] = useState<string>('');
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [selectedChannelId, setSelectedChannelId] = useState<string>('');
  const [requiredLevel, setRequiredLevel] = useState<'god_mode' | 'admin' | 'target' | 'user'>('user');
  const [showGrantModal, setShowGrantModal] = useState<boolean>(false);

  // Load Discord data
  const discordGuilds = useDiscordGuilds();
  const guildMembers = useDiscordGuildMembers(selectedGuildId, memberSearch, !!selectedGuildId);
  const guildChannels = useDiscordGuildChannels(selectedGuildId, !!selectedGuildId);
  const guildPermissions = useGuildPermissions(selectedGuildId, !!selectedGuildId);
  
  // Permission operations
  const { grantPermission, revokePermission, isLoading: operationsLoading, error: operationsError } = usePermissionOperations();

  // Get selected guild and member info for display
  const selectedGuild = useMemo(() => 
    discordGuilds.data?.guilds?.find(g => g.id === selectedGuildId),
    [discordGuilds.data?.guilds, selectedGuildId]
  );

  const selectedMember = useMemo(() => 
    guildMembers.data?.members?.find(m => m.id === selectedMemberId),
    [guildMembers.data?.members, selectedMemberId]
  );

  // Permission check for selected user
  const permissionCheck = usePermissionCheck(
    {
      userId: selectedMemberId,
      guildId: selectedGuildId,
      channelId: selectedChannelId || undefined,
      botName: selectedBot,
      requiredLevel,
    },
    !!selectedMemberId && !!selectedGuildId
  );

  // User level check
  const userLevelCheck = useUserLevel(selectedMemberId, selectedGuildId, !!selectedMemberId && !!selectedGuildId);

  // Channel permission check
  const channelPermissionCheck = useChannelPermissionCheck(
    {
      guildId: selectedGuildId,
      channelId: selectedChannelId,
      botName: selectedBot,
    },
    !!selectedChannelId && !!selectedGuildId
  );

  const handleGrantPermission = async (permissionLevel: 'god_mode' | 'admin' | 'target' | 'user') => {
    if (!selectedMemberId || !selectedGuildId) return;
    
    try {
      await grantPermission.mutateAsync({
        userId: selectedMemberId,
        guildId: selectedGuildId,
        botName: selectedBot,
        permissionLevel,
        grantedBy: user?.discordId || '',
        channelId: selectedChannelId || undefined,
        notes: `Granted via Permissions UI at ${new Date().toISOString()}`
      });
      setShowGrantModal(false);
    } catch (error) {
      console.error('Failed to grant permission:', error);
    }
  };

  const handleRevokePermission = async (permissionId: string) => {
    try {
      await revokePermission.mutateAsync({
        permissionId,
        revokedBy: user?.discordId || ''
      });
    } catch (error) {
      console.error('Failed to revoke permission:', error);
    }
  };

  const handleRefresh = () => {
    discordGuilds.refetch();
    if (selectedGuildId) {
      guildMembers.refetch();
      guildChannels.refetch();
      guildPermissions.refetch();
    }
    if (selectedMemberId && selectedGuildId) {
      permissionCheck.refetch();
      userLevelCheck.refetch();
    }
    if (selectedChannelId && selectedGuildId) {
      channelPermissionCheck.refetch();
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center mb-2">
            <ShieldIcon className="mr-3 text-blue-400" size={32} />
            Permission Management
          </h1>
          <p className="text-gray-400">
            Manage user permissions across Discord servers and bots. Select a server to view and modify user permissions.
          </p>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={discordGuilds.isLoading || operationsLoading}
          className="flex items-center"
        >
          <RefreshCwIcon 
            size={16} 
            className={`mr-2 ${(discordGuilds.isLoading || operationsLoading) ? 'animate-spin' : ''}`} 
          />
          Refresh Data
        </Button>
      </div>

      {/* Error Display */}
      {(discordGuilds.error || guildMembers.error || operationsError) && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircleIcon size={18} className="mr-2" />
          <span>
            {discordGuilds.error && 'Failed to load Discord servers. '}
            {guildMembers.error && 'Failed to load server members. '}
            {operationsError && 'Permission operation failed. '}
            Try refreshing the page.
          </span>
        </Alert>
      )}

      {/* Server Selection */}
      <Card className="mb-6">
        <div className="p-6">
          <h2 className="text-xl font-medium mb-4 flex items-center">
            <ServerIcon size={20} className="mr-2 text-blue-400" />
            Server Selection
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Discord Server
                <span className="text-red-400 ml-1">*</span>
              </label>
              {discordGuilds.isLoading ? (
                <div className="flex items-center justify-center h-10 bg-gray-700 rounded-md">
                  <Loader2 className="animate-spin" size={16} />
                  <span className="ml-2 text-sm text-gray-400">Loading servers...</span>
                </div>
              ) : (
                <Select
                  value={selectedGuildId}
                  onValueChange={(value) => {
                    setSelectedGuildId(value);
                    setSelectedMemberId('');
                    setSelectedChannelId('');
                    setMemberSearch('');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a server..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Select a server...</SelectItem>
                    {discordGuilds.data?.guilds?.map(guild => (
                      <SelectItem key={guild.id} value={guild.id}>
                        {guild.name} ({guild.memberCount} members)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Bot</label>
              <Select
                value={selectedBot}
                onValueChange={(value) => setSelectedBot(value as SupportedBotName)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hotel-bull">🏨 Hotel Bull (Roasting Bot)</SelectItem>
                  <SelectItem value="praise-kink">💕 Praise Kink (Positivity Bot)</SelectItem>
                  <SelectItem value="dungeon">⚔️ Dungeon (Game Bot)</SelectItem>
                  <SelectItem value="valerie">✨ Valerie (Voice Prototype)</SelectItem>
                  <SelectItem value="reminder-bot">⏰ Reminder Bot (Passive-Aggressive Reminders)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {selectedGuild && (
            <div className="mt-4 p-3 bg-gray-700 rounded-lg">
              <div className="text-sm text-gray-300">
                <strong>Selected Server:</strong> {selectedGuild.name} • 
                <strong> Members:</strong> {selectedGuild.memberCount} • 
                <strong> Available Bots:</strong> {selectedGuild.bots.join(', ')}
              </div>
            </div>
          )}
        </div>
      </Card>

      {selectedGuildId && (
        <>
          {/* User Selection & Permission Check */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* User Selection */}
            <Card>
              <div className="p-6">
                <h2 className="text-xl font-medium mb-4 flex items-center">
                  <UserIcon size={20} className="mr-2 text-green-400" />
                  User Selection
                </h2>
                
                {/* Member Search */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Search Members
                  </label>
                  <Input
                    type="text"
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    placeholder="Type to search for users..."
                  />
                </div>

                {/* Member List */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Select User
                  </label>
                  {guildMembers.isLoading ? (
                    <div className="flex items-center justify-center h-32 bg-gray-700 rounded-md">
                      <Loader2 className="animate-spin" size={20} />
                      <span className="ml-2 text-gray-400">Loading members...</span>
                    </div>
                  ) : (
                    <div className="max-h-64 overflow-y-auto bg-gray-700 rounded-md border border-gray-600">
                      {guildMembers.data?.members?.map(member => (
                        <button
                          key={member.id}
                          onClick={() => setSelectedMemberId(member.id)}
                          className={`w-full text-left p-3 hover:bg-gray-600 flex items-center transition-colors ${
                            selectedMemberId === member.id ? 'bg-blue-600' : ''
                          }`}
                        >
                          {member.avatar && (
                            <img
                              src={member.avatar}
                              alt=""
                              className="w-8 h-8 rounded-full mr-3"
                            />
                          )}
                          <div className="flex-1">
                            <div className="font-medium text-white">
                              {member.displayName}
                              {member.bot && <Badge variant="default" className="ml-2">BOT</Badge>}
                            </div>
                            <div className="text-sm text-gray-400">@{member.username}</div>
                          </div>
                        </button>
                      ))}
                      {(!guildMembers.data?.members || guildMembers.data.members.length === 0) && (
                        <div className="p-4 text-center text-gray-400">
                          No members found
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Selected User Info */}
                {selectedMember && (
                  <div className="p-4 bg-gray-700 rounded-lg">
                    <h3 className="font-medium mb-2">Selected User</h3>
                    <div className="flex items-center">
                      {selectedMember.avatar && (
                        <img
                          src={selectedMember.avatar}
                          alt=""
                          className="w-10 h-10 rounded-full mr-3"
                        />
                      )}
                      <div>
                        <div className="font-medium">{selectedMember.displayName}</div>
                        <div className="text-sm text-gray-400">@{selectedMember.username}</div>
                        <div className="text-xs text-gray-500">
                          {selectedMember.roles.length} roles • Joined {selectedMember.joinedAt ? new Date(selectedMember.joinedAt).toLocaleDateString() : 'Unknown'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Permission Check & Actions */}
            <Card>
              <div className="p-6">
                <h2 className="text-xl font-medium mb-4 flex items-center">
                  <EyeIcon size={20} className="mr-2 text-purple-400" />
                  Permission Check
                </h2>

                {selectedMember ? (
                  <div className="space-y-4">
                    {/* Current Permission Level */}
                    <div className="bg-gray-700 p-4 rounded-lg">
                      <h3 className="font-medium mb-2 flex items-center">
                        Current Permission Level
                        {userLevelCheck.isLoading && <Loader2 className="ml-2 animate-spin" size={16} />}
                      </h3>
                      {userLevelCheck.data ? (
                        <div className="flex items-center justify-between">
                          <Badge variant="default" className={getPermissionLevelInfo(userLevelCheck.data.level).bgColor + ' ' + getPermissionLevelInfo(userLevelCheck.data.level).color}>
                            {getPermissionLevelInfo(userLevelCheck.data.level).label}
                          </Badge>
                          <div className="text-sm text-gray-400">
                            {getPermissionLevelInfo(userLevelCheck.data.level).description}
                          </div>
                        </div>
                      ) : userLevelCheck.isLoading ? (
                        <div className="text-gray-400">Checking user level...</div>
                      ) : (
                        <div className="text-gray-400">No permission level set</div>
                      )}
                    </div>

                    {/* Permission Test */}
                    <div className="bg-gray-700 p-4 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-medium">Permission Test</h3>
                        <Select
                          value={requiredLevel}
                          onValueChange={(value) => setRequiredLevel(value as 'god_mode' | 'admin' | 'target' | 'user')}
                        >
                          <SelectTrigger className="w-48">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">User Level</SelectItem>
                            <SelectItem value="target">Target Level</SelectItem>
                            <SelectItem value="admin">Admin Level</SelectItem>
                            <SelectItem value="god_mode">God Mode</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {permissionCheck.data ? (
                        <div className="flex items-center">
                          {permissionCheck.data.allowed ? (
                            <CheckCircleIcon className="text-green-400 mr-2" size={20} />
                          ) : (
                            <XCircleIcon className="text-red-400 mr-2" size={20} />
                          )}
                          <span className={`font-medium ${permissionCheck.data.allowed ? 'text-green-300' : 'text-red-300'}`}>
                            {permissionCheck.data.allowed ? 'Access Granted' : 'Access Denied'}
                          </span>
                        </div>
                      ) : permissionCheck.isLoading ? (
                        <div className="text-gray-400">Testing permissions...</div>
                      ) : (
                        <div className="text-gray-400">Select a user to test permissions</div>
                      )}
                    </div>

                    {/* Permission Actions */}
                    <div className="space-y-2">
                      <Button
                        onClick={() => setShowGrantModal(true)}
                        disabled={operationsLoading}
                        className="w-full"
                      >
                        <UserPlusIcon size={16} className="mr-2" />
                        Grant Permission
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-400 py-8">
                    <UserIcon size={32} className="mx-auto mb-2 opacity-50" />
                    <p>Select a user to view and manage their permissions</p>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Existing Permissions Table */}
          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-medium flex items-center">
                  <ShieldIcon size={20} className="mr-2 text-blue-400" />
                  Server Permissions ({selectedBot})
                </h2>
                {guildPermissions.data && (
                  <span className="text-sm text-gray-400">
                    {guildPermissions.data.total} permissions total
                  </span>
                )}
              </div>

              {guildPermissions.isLoading ? (
                <div className="text-center py-8">
                  <Loader2 className="animate-spin mx-auto mb-2" size={24} />
                  <div className="text-gray-400">Loading permissions...</div>
                </div>
              ) : guildPermissions.data?.permissions && guildPermissions.data.permissions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-gray-400 text-sm border-b border-gray-700">
                        <th className="pb-3 font-medium">User</th>
                        <th className="pb-3 font-medium">Permission Level</th>
                        <th className="pb-3 font-medium">Channel</th>
                        <th className="pb-3 font-medium">Granted</th>
                        <th className="pb-3 font-medium">Expires</th>
                        <th className="pb-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {guildPermissions.data.permissions.map((permission) => {
                        const levelInfo = getPermissionLevelInfo(permission.permissionLevel);
                        return (
                          <tr key={permission.id} className="hover:bg-gray-750 transition-colors">
                            <td className="py-4">
                              <div className="font-mono text-sm text-gray-300">
                                {permission.userId.substring(0, 8)}...
                              </div>
                            </td>
                            <td className="py-4">
                              <Badge variant="default" className={levelInfo.bgColor + ' ' + levelInfo.color}>
                                {levelInfo.label}
                              </Badge>
                            </td>
                            <td className="py-4">
                              {permission.channelId ? (
                                <span className="text-sm text-gray-400 font-mono">
                                  #{permission.channelId.substring(0, 8)}...
                                </span>
                              ) : (
                                <span className="text-sm text-gray-500">Server-wide</span>
                              )}
                            </td>
                            <td className="py-4">
                              <div className="text-sm text-gray-400">
                                {new Date(permission.grantedAt).toLocaleDateString()}
                              </div>
                            </td>
                            <td className="py-4">
                              {permission.expiresAt ? (
                                <div className="text-sm text-gray-400">
                                  {new Date(permission.expiresAt).toLocaleDateString()}
                                </div>
                              ) : (
                                <span className="text-sm text-gray-500">Never</span>
                              )}
                            </td>
                            <td className="py-4">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRevokePermission(permission.id)}
                                disabled={operationsLoading}
                                title="Revoke Permission"
                              >
                                <TrashIcon size={16} />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <ShieldIcon size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No permissions set for this server and bot</p>
                  <p className="text-sm mt-1">Grant permissions to users to see them here</p>
                </div>
              )}
            </div>
          </Card>
        </>
      )}

      {/* Quick Grant Modal */}
      <Dialog open={showGrantModal} onOpenChange={setShowGrantModal}>
        <DialogContent>
          <DialogTitle>Grant Permission</DialogTitle>
          <DialogDescription>
            Grant permission to <strong>{selectedMember?.displayName}</strong> for <strong>{selectedBot}</strong>
          </DialogDescription>
          <div className="space-y-3">
            {(['user', 'target', 'admin', 'god_mode'] as const).map(level => {
              const levelInfo = getPermissionLevelInfo(level);
              return (
                <Button
                  key={level}
                  variant="outline"
                  onClick={() => handleGrantPermission(level)}
                  disabled={operationsLoading}
                  className="w-full justify-start"
                >
                  <div className="flex items-center justify-between w-full">
                    <span className={`font-medium ${levelInfo.color}`}>
                      {levelInfo.label}
                    </span>
                    <span className="text-sm text-gray-400">
                      {levelInfo.description}
                    </span>
                  </div>
                </Button>
              );
            })}
          </div>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setShowGrantModal(false)}
              disabled={operationsLoading}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
